import { useEffect, useRef } from "react";
import { toast } from "react-hot-toast";

import objectFromBarcode, { BarcodeObject } from "util/objectFromBarcode";

const useBarcodeScanner = (
  callback: (barcode: BarcodeObject) => false | void
) => {
  const open = useRef(false);
  const inProgressBarcode = useRef("");

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const endCharacter = {
      key: "Enter",
      code: "Enter",
    };
    const escCharacter = {
      key: "Escape",
      code: "Escape",
    };

    const isActiveInput = () => {
      switch (document.activeElement?.tagName) {
        case "TEXTAREA":
        case "SELECT":
          return true;
        case "INPUT":
          return document.activeElement?.getAttribute("type") === "radio" ||
            document.activeElement?.getAttribute("type") === "checkbox"
            ? false
            : true;
        default:
          return false;
      }
    };

    const captureKeyboard = (e: KeyboardEvent) => {
      if (
        isActiveInput() ||
        e.key === escCharacter.key ||
        e.code === escCharacter.code
      ) {
        inProgressBarcode.current = "";
        open.current = false;
        return;
      }
      if (open.current) {
        e.preventDefault();
        e.stopPropagation();

        if (e.key === endCharacter.key || e.code === endCharacter.code) {
          open.current = false;

          const object = objectFromBarcode(inProgressBarcode.current);
          const callbackResult = callbackRef.current(object);

          if (callbackResult === false) {
            const capitalizedType =
              object.object_type.charAt(0).toUpperCase() +
              object.object_type.slice(1);
            toast.error(
              `Scanned ${capitalizedType}\nNo action to perform at this station`
            );
          }

          inProgressBarcode.current = "";
        } else {
          if (e.key !== "Shift")
            inProgressBarcode.current = inProgressBarcode.current + e.key;
        }
      } else {
        if (e.key === "<") {
          open.current = true;
          e.preventDefault();
          e.stopPropagation();
          inProgressBarcode.current = "<";

          // Unfocus any "button", "radio", or "checkbox" element
          if (
            document.activeElement?.tagName === "BUTTON" ||
            (document.activeElement?.tagName === "INPUT" &&
              (document.activeElement?.getAttribute("type") === "radio" ||
                document.activeElement?.getAttribute("type") === "checkbox"))
          ) {
            (
              document.activeElement as HTMLButtonElement | HTMLInputElement
            ).blur();
          }
        }
      }
    };

    document.addEventListener("keydown", captureKeyboard);

    return () => {
      document.removeEventListener("keydown", captureKeyboard);
    };
  }, []);
};

export default useBarcodeScanner;
